import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, X, Send, Cookie, Settings, Eye, Clock } from 'lucide-react';
import logger from '../../utils/logger';
import brand from '../../config/brand';

const Cookies = () => {
  const { t } = useTranslation('public');
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });

  const handleContactSubmit = (e) => {
    e.preventDefault();
    logger.debug('Formulaire contact depuis Cookies:', contactForm);
    alert('Votre message a été envoyé avec succès !');
    setShowContactModal(false);
    setContactForm({ nom: '', prenom: '', email: '', telephone: '', message: '' });
  };

  const cookiesData = [
    {
      category: `Cookies ${brand.name}`,
      purpose: 'Fonctionnement du site',
      cookies: ['session_id', 'preferences_user', 'auth_token'],
      duration: '13 mois maximum'
    },
    {
      category: 'Google Analytics',
      purpose: 'Mesure d\'audience',
      cookies: ['_ga', '_gid', '_gat'],
      duration: '13 mois maximum'
    },
    {
      category: 'Google Tag Manager',
      purpose: 'Gestion des balises',
      cookies: ['_gtm'],
      duration: '13 mois maximum'
    },
    {
      category: 'Hotjar',
      purpose: 'Analyse comportementale',
      cookies: ['_hjid', '_hjSessionUser'],
      duration: '13 mois maximum'
    },
    {
      category: 'Publicité',
      purpose: 'Personnalisation publicitaire',
      cookies: ['Facebook Ads', 'Google Ads', 'LinkedIn', 'Bing', 'Twitter'],
      duration: '13 mois maximum'
    },
    {
      category: 'Mailjet',
      purpose: 'Envoi d\'emails',
      cookies: ['mailjet_campaign'],
      duration: '13 mois maximum'
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            
            {/* Bouton Contact flottant */}
            <div className="fixed bottom-6 right-6 z-40">
              <button
                onClick={() => setShowContactModal(true)}
                className="bg-brand-primary text-white p-4 rounded-full shadow-lg hover:bg-brand-primary-hover transition-all duration-300 hover:scale-110"
                title="Nous contacter"
              >
                <MessageSquare size={24} />
              </button>
            </div>

            <div className="flex items-center mb-8">
              <Cookie className="text-brand-primary mr-4" size={36} />
              <h1 className="text-3xl font-bold text-brand-text">{t('legal.cookies.title')}</h1>
            </div>
            
            <div className="space-y-8 text-gray-700 leading-relaxed">
              
              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4 flex items-center">
                  <Eye className="mr-2" size={20} />
                  {t('legal.cookies.whatIs')}
                </h2>
                <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-brand-primary">
                  <p className="mb-4">
                    {t('legal.cookies.whatIsText')}
                  </p>
                  <ul className="list-disc list-inside space-y-2">
                    <li>{t('legal.cookies.whatIsItems.preferences')}</li>
                    <li>{t('legal.cookies.whatIsItems.experience')}</li>
                    <li>{t('legal.cookies.whatIsItems.audience')}</li>
                    <li>{t('legal.cookies.whatIsItems.personalize')}</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.objectives')}</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">{t('legal.cookies.cookieTypes.essential.title')}</h4>
                    <p className="text-sm text-green-700">
                      {t('legal.cookies.cookieTypes.essential.description')}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">{t('legal.cookies.cookieTypes.analytics.title')}</h4>
                    <p className="text-sm text-blue-700">
                      {t('legal.cookies.cookieTypes.analytics.description')}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h4 className="font-semibold text-orange-800 mb-2">{t('legal.cookies.cookieTypes.marketing.title')}</h4>
                    <p className="text-sm text-orange-700">
                      {t('legal.cookies.cookieTypes.marketing.description')}
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.usedCookies')}</h2>
                <div className="space-y-4">
                  {cookiesData.map((category, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-gray-900">{category.category}</h4>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded flex items-center">
                          <Clock size={12} className="mr-1" />
                          {category.duration}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>{t('legal.cookies.purpose')}</strong> {category.purpose}
                      </p>
                      <p className="text-xs text-gray-500">
                        <strong>{t('legal.cookies.cookiesUsed')}</strong> {Array.isArray(category.cookies) ? category.cookies.join(', ') : category.cookies}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4 flex items-center">
                  <Settings className="mr-2" size={20} />
                  {t('legal.cookies.management')}
                </h2>
                <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-400">
                  <h4 className="font-semibold mb-3">{t('legal.cookies.managementTitle')}</h4>
                  <p className="mb-4">
                    {t('legal.cookies.managementText')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li><strong>{t('legal.cookies.managementItems.banner')}</strong></li>
                    <li><strong>{t('legal.cookies.managementItems.browser')}</strong></li>
                    <li><strong>{t('legal.cookies.managementItems.tools')}</strong></li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.browserConfig')}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Google Chrome</h4>
                    <p className="text-sm">Paramètres → Confidentialité et sécurité → Cookies et autres données de sites</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Mozilla Firefox</h4>
                    <p className="text-sm">Options → Vie privée et sécurité → Cookies et données de sites</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Safari</h4>
                    <p className="text-sm">Préférences → Confidentialité → Cookies et données de sites web</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2">Microsoft Edge</h4>
                    <p className="text-sm">Paramètres → Confidentialité, recherche et services → Cookies</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.impact')}</h2>
                <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-400">
                  <h4 className="font-semibold text-red-800 mb-3">{t('legal.cookies.impactWarning')}</h4>
                  <p className="mb-4">
                    {t('legal.cookies.impactText')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-red-700">
                    <li>{t('legal.cookies.impactItems.preferences')}</li>
                    <li>{t('legal.cookies.impactItems.login')}</li>
                    <li>{t('legal.cookies.impactItems.features')}</li>
                    <li>{t('legal.cookies.impactItems.content')}</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.duration')}</h2>
                <p>
                  {t('legal.cookies.durationText')}
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-brand-primary mb-4">{t('legal.cookies.contactTitle')}</h2>
                <div className="bg-brand-primary bg-opacity-10 p-6 rounded-lg">
                  <p className="mb-2">
                    {t('legal.cookies.contactText')}
                  </p>
                  <p>
                    <strong>Email :</strong> gestion@asarcsg.com
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>

      {/* Modal Contact */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-brand-text">{t('legal.contactUs')}</h2>
                <button
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.nom}
                      onChange={(e) => setContactForm({...contactForm, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                    <input
                      type="text"
                      required
                      value={contactForm.prenom}
                      onChange={(e) => setContactForm({...contactForm, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                  <input
                    type="tel"
                    required
                    value={contactForm.telephone}
                    onChange={(e) => setContactForm({...contactForm, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea
                    rows="4"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
                    placeholder="Votre message..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-brand-primary text-white py-3 rounded-lg font-semibold hover:bg-brand-primary-hover transition-colors flex items-center justify-center"
                >
                  <Send className="mr-2" size={18} />
                  Envoyer le message
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Cookies;